import { BRAND_CONFIGS, detectBrandFromHostname, swapApex } from '../../config/branding';
import delegationState from '../../utils/delegation';

const IMPERSONATION_PROVIDER_ID_PARAM = 'impersonationProviderId';
const IMPERSONATION_PROVIDER_NAME_PARAM = 'impersonationProviderName';

export const IMPERSONATION_STORAGE_KEY = 'impersonationState';

export type ImpersonationHandoffState = {
  providerId: number;
  providerName?: string;
  /**
   * The Clerk user whose session this impersonation belongs to. Absent on a fresh
   * handoff from the URL hash, which belongs to whoever signs in to receive it.
   */
  ownerUserId?: string;
};

// True from the moment a handoff is read from the URL hash until a sign-in consumes
// it. A signed-out event in that window is the sign-in still settling (the HLX/MCV
// sync can land signed-out first), not a session ending, so it must not drop it.
let freshHandoffPending = false;

const normalizeProviderId = (value: unknown): number | undefined => {
  const providerId = Number(value);
  return Number.isInteger(providerId) && providerId > 0 ? providerId : undefined;
};

const normalizeProviderName = (value: unknown): string | undefined =>
  typeof value === 'string' && value.trim().length > 0 ? value : undefined;

const parseHandoffParams = (params: URLSearchParams): ImpersonationHandoffState | undefined => {
  const providerId = normalizeProviderId(params.get(IMPERSONATION_PROVIDER_ID_PARAM));
  if (providerId == null) return undefined;

  return {
    providerId,
    providerName: normalizeProviderName(params.get(IMPERSONATION_PROVIDER_NAME_PARAM)),
  };
};

const removeHandoffHashParams = () => {
  if (typeof window === 'undefined' || typeof window.history?.replaceState !== 'function') return;

  const params = new URLSearchParams(window.location.hash.replace(/^#/, ''));
  if (!params.has(IMPERSONATION_PROVIDER_ID_PARAM)) return;

  params.delete(IMPERSONATION_PROVIDER_ID_PARAM);
  params.delete(IMPERSONATION_PROVIDER_NAME_PARAM);
  const remainingHash = params.toString();
  window.history.replaceState(
    window.history.state,
    document.title,
    `${window.location.pathname}${window.location.search}${remainingHash ? `#${remainingHash}` : ''}`
  );
};

export const buildImpersonationHandoffHash = ({
  providerId,
  providerName,
}: ImpersonationHandoffState): string => {
  const normalizedProviderId = normalizeProviderId(providerId);
  if (normalizedProviderId == null) return '';

  const params = new URLSearchParams();
  params.set(IMPERSONATION_PROVIDER_ID_PARAM, String(normalizedProviderId));
  const normalizedProviderName = normalizeProviderName(providerName);
  if (normalizedProviderName) {
    params.set(IMPERSONATION_PROVIDER_NAME_PARAM, normalizedProviderName);
  }

  return `#${params.toString()}`;
};

export const readStoredImpersonationState = (): ImpersonationHandoffState | undefined => {
  if (typeof sessionStorage === 'undefined') return undefined;

  try {
    const savedState = sessionStorage.getItem(IMPERSONATION_STORAGE_KEY);
    if (!savedState) return undefined;
    const parsed = JSON.parse(savedState) as Partial<ImpersonationHandoffState>;
    const providerId = normalizeProviderId(parsed.providerId);
    if (providerId == null) return undefined;

    return {
      providerId,
      providerName: normalizeProviderName(parsed.providerName),
      ownerUserId: typeof parsed.ownerUserId === 'string' ? parsed.ownerUserId : undefined,
    };
  } catch (e) {
    console.error('Failed to restore impersonation state:', e);
    return undefined;
  }
};

export const persistImpersonationState = (state: Partial<ImpersonationHandoffState>) => {
  if (typeof sessionStorage === 'undefined') return;
  const providerId = normalizeProviderId(state.providerId);
  if (providerId == null) return;

  sessionStorage.setItem(
    IMPERSONATION_STORAGE_KEY,
    JSON.stringify({
      providerId,
      providerName: normalizeProviderName(state.providerName),
      ownerUserId: state.ownerUserId,
    })
  );
};

export const clearImpersonationState = () => {
  freshHandoffPending = false;
  if (typeof sessionStorage === 'undefined') return;
  sessionStorage.removeItem(IMPERSONATION_STORAGE_KEY);
};

/**
 * Reads the pending impersonation. `signedInUserId` is the Clerk user who is signing
 * in to receive it: a stored record owned by someone else is that other account's
 * leftover and is discarded rather than handed over. Boot priming passes nothing.
 */
export const consumeImpersonationHandoff = (
  signedInUserId?: string
): ImpersonationHandoffState | undefined => {
  if (typeof window === 'undefined') return readStoredImpersonationState();

  const hashState = parseHandoffParams(new URLSearchParams(window.location.hash.replace(/^#/, '')));
  if (hashState) {
    persistImpersonationState(hashState);
    removeHandoffHashParams();
    freshHandoffPending = signedInUserId === undefined;
    return hashState;
  }

  const stored = readStoredImpersonationState();
  if (signedInUserId !== undefined) {
    freshHandoffPending = false;
    if (stored?.ownerUserId && stored.ownerUserId !== signedInUserId) {
      clearImpersonationState();
      return undefined;
    }
  }
  return stored;
};

/**
 * A confirmed signed-out page load: drop any impersonation left behind by an earlier
 * session, except a handoff that arrived on this very load and has not met its
 * sign-in yet. Returns whether anything was cleared.
 */
export const clearStaleImpersonationOnSignedOut = (): boolean => {
  if (freshHandoffPending) return false;
  clearImpersonationState();
  delegationState.setActingAsProvider(undefined);
  return true;
};

export const buildEnterImpersonationUrl = (
  state: ImpersonationHandoffState,
  hostname: string = typeof window !== 'undefined' ? window.location.hostname : '',
  path = '/vault/dashboard'
): string => {
  const handoffHash = buildImpersonationHandoffHash(state);
  const targetPath = `${path}${handoffHash}`;
  const hostBrand = detectBrandFromHostname(hostname);
  if (hostBrand !== 'hlx') return targetPath;

  let target = swapApex(hostname, BRAND_CONFIGS.hlx.domain, BRAND_CONFIGS.mcv.domain);
  if (target === hostname) return targetPath;
  // A bare HLX apex swaps to the bare MCV apex, which doesn't serve the app
  // (same fallback as postAuthRedirect and the main.tsx bootstrap).
  if (target === BRAND_CONFIGS.mcv.domain) {
    target = `prod.${BRAND_CONFIGS.mcv.domain}`;
  }

  return `https://${target}${targetPath}`;
};

// Exit-impersonation target. In multi-domain prod (`*.medcredvault.com` vs
// `*.healthcarelx.com`) a relative `/hlx/...` assignment from the vault
// host stays on the vault host, where HLX routes aren't mounted. Compute the
// absolute HLX-host URL up front so the exit flow lands directly on the HLX
// host instead of bouncing through `DefaultRoleRedirect`. Falls back to a
// same-origin path in dev / single-host environments.
export const buildExitImpersonationUrl = (
  path = '/hlx/provider-associations',
  hostname: string = typeof window !== 'undefined' ? window.location.hostname : ''
): string => {
  const hostBrand = detectBrandFromHostname(hostname);
  if (hostBrand !== 'mcv') return path;

  const mcvDomain = BRAND_CONFIGS.mcv.domain;
  if (!hostname.endsWith(mcvDomain)) return path;

  const target = hostname.slice(0, hostname.length - mcvDomain.length) + BRAND_CONFIGS.hlx.domain;
  if (target === hostname) return path;

  return `https://${target}${path}`;
};

// Bootstrap helper: consume any pending impersonation handoff and prime the
// axios request interceptor's delegation state. Call this once during app
// boot, before any subtree mounts. Without this priming, the dashboard's
// data-fetch effects fire before the XState user machine enters
// `$_AUTHENTICATED_IMPERSONATING`, so the `X-Acting-As-Provider` header is
// missing on every vault API call and the backend returns 401.
export const primeImpersonationDelegation = (): ImpersonationHandoffState | undefined => {
  const handoff = consumeImpersonationHandoff();
  if (handoff) {
    delegationState.setActingAsProvider(handoff.providerId);
  }
  return handoff;
};
